import { Modal, useToastContext } from "@ttc3k/trekker";
import { EnumOwnerType, PersonDetailsQuery } from "gql/generated";
import { Flex, Grid } from "styled-system/jsx";
import {
  CommonContactInfo,
  NotificationPreferences,
  PersonContactInfoModalFooter,
  PersonContactInfoModalHeader,
} from "./components";
import { FormProvider } from "react-hook-form";
import { usePersonContactInfoForm } from "./usePersonContactInfoForm";
import { AdditionalContactInfo } from "./components/AdditionalContactInfo/AdditionalContactInfo";
import { useTranslation } from "react-i18next";

type PersonContactInfoModalProps = {
  isOpen: boolean;
  onClose: () => void;
  person: PersonDetailsQuery["personDetails"];
  entityContext?: { entityId: string; type: EnumOwnerType };
  placement?: "leftPane" | "rightPane";
  variant?: "default" | "editProfile";
  initialSection?: "notifications";
};

export const PersonContactInfoModal = ({
  isOpen,
  onClose,
  person,
  entityContext,
  placement,
  variant = "default",
  initialSection,
}: PersonContactInfoModalProps) => {
  const { t } = useTranslation("people", {
    keyPrefix: "PERSON_CONTACT_INFO_MODAL",
  });
  const { t: tCore } = useTranslation("core");
  const { toastFactory } = useToastContext();

  const { form, onSubmit } = usePersonContactInfoForm({
    person,
    entityContext,
    onSuccess: didSubmit => {
      if (didSubmit) {
        toastFactory.create({
          title: tCore("SUCCESS"),
          description: t("SUCCESS"),
        });
      }
      onClose();
    },
    onError: (message: string) => {
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description: message,
      });
    },
  });

  const handleCloseChange = () => {
    form.reset();
    onClose();
  };

  if (!person) return null;

  const formId = "person-contact-info-form";

  return (
    <Modal
      open={isOpen}
      onOpenChange={handleCloseChange}
      zIndex="max"
      hideCloseTrigger={true}
      {...(placement && { placement })}
    >
      <Grid
        gridTemplateRows={"1fr 88px"}
        columnGap={0}
        gridColumnGap={0}
        gap={0}
        w="full"
        maxW={{ md: "520px" }}
        h="full"
        bgColor="#FFF"
      >
        <FormProvider {...form}>
          <form id={formId} onSubmit={onSubmit} style={{ overflowY: "auto" }}>
            <Flex flexDir="column" gap="500" p="500">
              <PersonContactInfoModalHeader
                onBackClick={handleCloseChange}
                variant={variant}
              />
              <CommonContactInfo
                isUserClaimed={!!(person as any).isUserClaimed}
                isUserInvited={!!(person as any).isUserInvited}
                personPhoto={person.personInfo?.photo}
              />
              {variant === "editProfile" ? (
                <NotificationPreferences
                  focusOnMount={initialSection === "notifications"}
                />
              ) : (
                <AdditionalContactInfo />
              )}
            </Flex>
          </form>
        </FormProvider>

        <PersonContactInfoModalFooter
          isSaving={form.formState.isSubmitting || form.formState.isLoading}
          onCancel={handleCloseChange}
          onSave={onSubmit}
        />
      </Grid>
    </Modal>
  );
};
